'use client';

import { useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';

import { searchHealthResources } from '@/app/(main)/_components/search-modal/_services/search-health-resources.service';
import { searchHealthResourcesByType } from '@/app/(main)/_components/search-modal/_services/search-health-resources-by-type.service';
import { AllSearchResults, SearchType } from '@/app/(main)/_components/search-modal/search-modal.types';
import {
  clearSearchState,
  consumePreselectedFilter,
  removeSelectedSearch,
  setFetchedResults,
  setSearchTerm,
  useSearchState,
} from '@/store/search/search.store';
import { useDebounceFn } from '@/hooks/use-debounce.hook';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { getLogger } from '@/logger/logger';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { userLocationStore } from '@/store/user-location.store';
import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { resolveCitySlug } from '@/utils/formatters/city/resolve-city-slug.util';
import { useFeatureFlag } from '@/feature-flags/hooks/use-feature-flag.hook';
import { SECTION_RESULT_LIMIT } from '@/app/(main)/_components/search-modal/_consts/search-results.consts';

const EMPTY_RESULTS: AllSearchResults = {
  [SearchType.ALL]: { results: [], total: 0 },
  [SearchType.PROCEDURE]: { results: [], total: 0 },
  [SearchType.PROFESSIONAL]: { results: [], total: 0 },
  [SearchType.PARTNER]: { results: [], total: 0 },
};

async function fetchMixedResults(term: string): Promise<AllSearchResults> {
  const [all, procedures, professionals, partners] = await Promise.all([
    searchHealthResources(term),
    searchHealthResourcesByType(term, SearchType.PROCEDURE),
    searchHealthResourcesByType(term, SearchType.PROFESSIONAL),
    searchHealthResourcesByType(term, SearchType.PARTNER),
  ]);

  return {
    [SearchType.ALL]: all,
    [SearchType.PROCEDURE]: procedures,
    [SearchType.PROFESSIONAL]: professionals,
    [SearchType.PARTNER]: partners,
  };
}

async function fetchCategorizedResults(term: string): Promise<AllSearchResults> {
  const [procedures, professionals, partners] = await Promise.all([
    searchHealthResourcesByType(term, SearchType.PROCEDURE, 1, SECTION_RESULT_LIMIT),
    searchHealthResourcesByType(term, SearchType.PROFESSIONAL, 1, SECTION_RESULT_LIMIT),
    searchHealthResourcesByType(term, SearchType.PARTNER, 1, SECTION_RESULT_LIMIT),
  ]);

  return {
    [SearchType.ALL]: {
      results: [...procedures.results, ...professionals.results, ...partners.results],
      total: procedures.total + professionals.total + partners.total,
    },
    [SearchType.PROCEDURE]: procedures,
    [SearchType.PROFESSIONAL]: professionals,
    [SearchType.PARTNER]: partners,
  };
}

export function useSearchModalFetch() {
  const searchState = useStore(useSearchState, (state) => state);
  const userLocation = useStore(userLocationStore, (state) => state.userLocation);
  const { getParam } = useSearchParams([PersistedUserNavigationSearchParams.CIDADE]);
  const { value: newSearchModalDesign } = useFeatureFlag('newSearchModalDesign');
  const analytics = getAnalyticsPlugin();

  const chooseInitialResults = (): AllSearchResults =>
    searchState.fetchedResults ?? EMPTY_RESULTS;

  const chooseShouldShowSearchResults = (): boolean =>
    searchState.fetchedResults
      ? Object.values(searchState.fetchedResults).some((r) => r.results.length > 0)
      : false;

  const [inputValue, setInputValue] = useState('');
  const [searchDisplayValue, setSearchDisplayValue] = useState('');
  const [shouldShowSearchResults, setShouldShowSearchResults] = useState(chooseShouldShowSearchResults());
  const [shouldShowEmptyState, setShouldShowEmptyState] = useState(false);
  const [searchResultIsLoading, setSearchResultIsLoading] = useState(false);
  const [allResults, setAllResults] = useState<AllSearchResults>(chooseInitialResults());

  const activeRequestId = useRef(0);
  const lastTrackedSearchTerm = useRef('');
  const debouncedSetInputValue = useDebounceFn(setInputValue, 400);

  const trackSearch = (term: string, hasAnyResult: boolean) => {
    if (lastTrackedSearchTerm.current === term) {
      return;
    }

    lastTrackedSearchTerm.current = term;

    analytics.track(
      hasAnyResult
        ? AnalyticsEnum.SEARCHED_PROCEDURES_MODAL_WITH_RESULTS
        : AnalyticsEnum.SEARCHED_PROCEDURES_MODAL_WITHOUT_RESULTS,
      {
        term,
        cidade: resolveCitySlug(getParam(PersistedUserNavigationSearchParams.CIDADE), userLocation),
      },
    );
  };

  useEffect(() => {
    if (searchState.searchTerm) {
      setInputValue(searchState.searchTerm);
      setSearchDisplayValue(searchState.searchTerm);
    }
  }, [searchState.searchTerm]);

  useEffect(() => {
    const currentRequestId = ++activeRequestId.current;

    if (inputValue.length < 3) {
      clearSearchState();
      setAllResults(EMPTY_RESULTS);
      setShouldShowSearchResults(false);
      setShouldShowEmptyState(false);
      setSearchResultIsLoading(false);
      return;
    }

    if (searchState.searchTerm === inputValue) {
      return;
    }

    async function fetchResults() {
      setAllResults(EMPTY_RESULTS);
      setShouldShowSearchResults(false);
      setShouldShowEmptyState(false);
      setSearchResultIsLoading(true);

      try {
        const results = newSearchModalDesign
          ? await fetchCategorizedResults(inputValue)
          : await fetchMixedResults(inputValue);

        if (currentRequestId !== activeRequestId.current) {
          return;
        }

        const hasAnyResult = Object.values(results).some((r) => r.total > 0);

        trackSearch(inputValue, hasAnyResult);

        if (hasAnyResult) {
          setShouldShowSearchResults(true);
          setShouldShowEmptyState(false);
        } else {
          setShouldShowEmptyState(true);
        }

        setFetchedResults(results);
        consumePreselectedFilter(results);
        setSearchTerm(inputValue);
        setAllResults(results);
      } catch (error) {
        getLogger('SearchModal - Default').error('Error fetching search results', {
          search: { term: inputValue, request_sequence: currentRequestId },
          error,
        });
        if (currentRequestId === activeRequestId.current) {
          setShouldShowEmptyState(true);
        }
      } finally {
        if (currentRequestId === activeRequestId.current) {
          setSearchResultIsLoading(false);
        }
      }
    }

    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetInputValue(e.target.value);
    setSearchDisplayValue(e.target.value);
  };

  const handleRemoveSelectedSearch = () => {
    removeSelectedSearch();
    analytics.track(AnalyticsEnum.CLICK_CLEAR_FILTERS_SEARCH_MODAL);
  };

  return {
    inputValue,
    searchDisplayValue,
    handleSearchInput,
    allResults,
    shouldShowSearchResults,
    shouldShowEmptyState,
    searchResultIsLoading,
    handleRemoveSelectedSearch,
    newSearchModalDesign,
  };
}
